import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500";
const lbl = "text-xs font-medium text-slate-500 mb-1 block";
const STATUS_COLOR = { requested: "#ef4444", confirmed: "#22c55e", completed: "#eab308", pending_payment: "#a855f7", rescheduled: "#3b82f6", cancelled: "#94a3b8", no_show: "#f97316" };
const statusColor = (s) => STATUS_COLOR[s] || "#1f6feb";
const fmt12 = (t) => { if (!t || !/^\d{1,2}:\d{2}/.test(t)) return t; const [h, m] = t.split(":").map(Number); const ap = h >= 12 ? "PM" : "AM"; const h12 = h % 12 === 0 ? 12 : h % 12; return `${h12}:${String(m).padStart(2, "0")} ${ap}`; };

const GCAL_BADGE = {
  synced:       { label: "Synced",      cls: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: Icons.CalendarCheck2 },
  pending:      { label: "Syncing…",    cls: "bg-blue-50 text-blue-600 border-blue-200",           icon: Icons.Loader2 },
  failed:       { label: "Sync failed", cls: "bg-amber-50 text-amber-700 border-amber-200",        icon: Icons.AlertTriangle },
  auth_failed:  { label: "Reconnect",   cls: "bg-rose-50 text-rose-700 border-rose-200",           icon: Icons.Link2Off },
  not_required: null,
};

function GcalSyncBadge({ appt, onRetry, retrying }) {
  const gc = appt.google_calendar || {};
  const status = gc.gcal_sync_status;
  const conf = GCAL_BADGE[status];
  if (!conf) return null;
  const Icon = conf.icon;
  const canRetry = status === "failed" && !gc.gcal_permanent_failure;
  const isAuth   = status === "auth_failed";
  return (
    <span className="inline-flex items-center gap-1" data-testid={`gcal-sync-badge-${appt.id}`}>
      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${conf.cls}`}>
        <Icon className={`h-3 w-3 ${status === "pending" ? "animate-spin" : ""}`} />
        {conf.label}
      </span>
      {canRetry && (
        <button onClick={(e) => { e.stopPropagation(); onRetry(appt.id); }}
          disabled={retrying === appt.id}
          data-testid={`gcal-retry-btn-${appt.id}`}
          title="Retry Google Calendar sync"
          className="text-[10px] border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 rounded px-1.5 py-0.5 disabled:opacity-50">
          {retrying === appt.id ? <Icons.Loader2 className="h-3 w-3 animate-spin inline" /> : "Retry"}
        </button>
      )}
      {isAuth && (
        <a href="/scheduling/settings" data-testid={`gcal-reconnect-link-${appt.id}`}
          className="text-[10px] border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 rounded px-1.5 py-0.5">
          Reconnect
        </a>
      )}
      {gc.html_link && status === "synced" && (
        <a href={gc.html_link} target="_blank" rel="noreferrer" data-testid={`gcal-open-link-${appt.id}`}
          title="Open in Google Calendar" className="text-blue-500 hover:text-blue-700">
          <Icons.ExternalLink className="h-3 w-3" />
        </a>
      )}
    </span>
  );
}

export default function SchedulingAppointments() {
  const [rows, setRows] = useState(null);
  const [showBook, setShowBook] = useState(false);
  const [filter, setFilter] = useState("");
  const [reschedReq, setReschedReq] = useState(null);
  const [reschedNote, setReschedNote] = useState("");
  const [reschedBusy, setReschedBusy] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get("appointment");

  const load = useCallback(() => {
    api.get("/scheduling/appointments").then((r) => setRows(r.data.appointments || [])).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Deep-link from the header alert dropdown: scroll to & highlight the requested appointment.
  const highlightRef = useCallback((node) => {
    if (node && highlightId) setTimeout(() => node.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  }, [highlightId]);

  const setStatus = async (id, status) => {
    try {
      await api.post(`/scheduling/appointments/${id}/status`, { status });
      toast.success(status === "confirmed" ? "Confirmed — the client has been notified." : `Marked ${status}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const retrySync = async (id) => {
    setRetrying(id);
    try {
      await api.post(`/scheduling/appointments/${id}/sync`);
      toast.success("Google Calendar sync retried.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync retry failed"); }
    finally { setRetrying(null); }
  };

  const sendRescheduleRequest = async () => {
    if (!reschedReq || reschedBusy) return;
    setReschedBusy(true);
    try {
      await api.post(`/scheduling/appointments/${reschedReq.id}/request-reschedule`, { note: reschedNote.trim() });
      toast.success("Reschedule request sent — the client has been asked to pick a new time.");
      setReschedReq(null); setReschedNote(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send request"); }
    finally { setReschedBusy(false); }
  };

  const list = (rows || []).filter((a) => !filter || a.status === filter);
  return (
    <div className="p-6 max-w-[1180px] mx-auto space-y-4" data-testid="scheduling-appointments">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl font-bold text-slate-900">Appointments</h1><p className="text-sm text-slate-500">Every appointment from the engine.</p></div>
        <button onClick={() => setShowBook(true)} data-testid="appt-new-btn" className="inline-flex items-center gap-2 bg-[#171B2E] text-white text-sm rounded-lg px-4 py-2.5"><Icons.CalendarPlus className="h-4 w-4 text-[#F5A623]" />Book Appointment</button>
      </div>
      <div className="flex gap-1.5 flex-wrap">
        {["", "requested", "confirmed", "completed", "cancelled", "no_show"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`appt-filter-${s || "all"}`} className={`text-xs px-3 py-1.5 rounded-full border ${filter === s ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`}>{s ? s.replace("_", " ") : "All"}</button>
        ))}
      </div>
      {rows === null ? <div className="py-10 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></div> :
      list.length === 0 ? <p className="text-sm text-slate-400">No appointments.</p> : (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm divide-y divide-slate-100">
          {list.map((a) => (
            <div key={a.id} ref={a.id === highlightId ? highlightRef : null} className={`flex items-center gap-3 p-3.5 transition-colors ${a.id === highlightId ? "bg-amber-50 ring-2 ring-amber-300 ring-inset rounded-lg" : ""}`} data-testid={`appt-row-${a.id}`}>
              <span className="w-1.5 h-10 rounded-full shrink-0" style={{ backgroundColor: statusColor(a.status) }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{a.meeting_type_name} · {a.contact_name || "Client"}</div>
                <div className="text-xs text-slate-400">{a.number} · {a.date} {fmt12(a.start_time)} ({a.timezone}) · {a.duration_min}m {a.payment_required ? `· $${a.price}` : ""}</div>
                {a.previous_start_time && (
                  <div className="text-[11px] text-blue-600 mt-0.5 inline-flex items-center gap-1" data-testid={`appt-reschedule-note-${a.id}`}>
                    <Icons.CalendarClock className="h-3 w-3" />
                    Rescheduled from <span className="line-through opacity-70">{a.previous_date} {fmt12(a.previous_start_time)}</span>
                    {a.rescheduled_at && <span className="text-blue-400">· {new Date(a.rescheduled_at).toLocaleDateString()}</span>}
                  </div>
                )}
                <div className="mt-0.5"><GcalSyncBadge appt={a} onRetry={retrySync} retrying={retrying} /></div>
              </div>
              {(a.zoom || {}).join_url && <a href={a.zoom.join_url} target="_blank" rel="noreferrer" className="text-xs text-sky-600 inline-flex items-center gap-1"><Icons.Video className="h-3.5 w-3.5" />Zoom</a>}
              <span className="text-[11px] px-2 py-0.5 rounded-full text-white capitalize" style={{ backgroundColor: statusColor(a.status) }}>{a.status.replace("_", " ")}</span>
              <div className="flex gap-1">
                {a.status === "requested" && <button onClick={() => setStatus(a.id, "confirmed")} data-testid={`appt-confirm-${a.id}`} className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-2.5 py-1.5"><Icons.CheckCircle2 className="h-3.5 w-3.5" />Accept</button>}
                {a.status !== "completed" && a.status !== "cancelled" && <button onClick={() => setStatus(a.id, "completed")} data-testid={`appt-complete-${a.id}`} title="Complete" className="p-1.5 hover:bg-yellow-50 rounded text-yellow-500"><Icons.Check className="h-4 w-4" /></button>}
                {["requested", "confirmed", "rescheduled"].includes(a.status) && <button onClick={() => { setReschedReq(a); setReschedNote(""); }} data-testid={`appt-request-reschedule-${a.id}`} title="Request reschedule" className="p-1.5 hover:bg-blue-50 rounded text-blue-500"><Icons.CalendarClock className="h-4 w-4" /></button>}
                {["requested", "confirmed", "rescheduled"].includes(a.status) && <button onClick={() => setStatus(a.id, "cancelled")} data-testid={`appt-cancel-${a.id}`} title="Cancel" className="p-1.5 hover:bg-rose-50 rounded text-rose-400"><Icons.X className="h-4 w-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}
      {showBook && <BookModal onClose={() => setShowBook(false)} onBooked={() => { setShowBook(false); load(); }} />}
      {reschedReq && (
        <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={() => setReschedReq(null)}>
          <div className="bg-white rounded-xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="appt-reschedule-request-modal">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Icons.CalendarClock className="h-4.5 w-4.5 text-blue-600" /></div>
              <div><h3 className="font-semibold text-slate-800">Request reschedule</h3><p className="text-xs text-slate-500">{reschedReq.meeting_type_name} · {reschedReq.contact_name || "Client"} · {reschedReq.date} {fmt12(reschedReq.start_time)}</p></div>
            </div>
            <p className="text-sm text-slate-600">This will <span className="font-medium">cancel this appointment</span> (freeing the slot), email {reschedReq.contact_name || "the client"} an apology, and invite them to pick a new time in their Secure Portal.</p>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Optional note to the client</label>
              <textarea value={reschedNote} onChange={(e) => setReschedNote(e.target.value)} data-testid="appt-reschedule-note-input" rows={3}
                placeholder="e.g. Mitch is out sick today — apologies for the inconvenience."
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 resize-none" />
              <p className="text-[11px] text-slate-400 mt-1">Leave blank to send the standard "due to unforeseen circumstances" message.</p>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setReschedReq(null)} data-testid="appt-reschedule-cancel-btn" className="flex-1 text-sm border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-lg px-3 py-2">Cancel</button>
              <button onClick={sendRescheduleRequest} disabled={reschedBusy} data-testid="appt-reschedule-send-btn" className="flex-1 inline-flex items-center justify-center gap-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 disabled:opacity-60">
                {reschedBusy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Send className="h-4 w-4" />}Send request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BookModal({ onClose, onBooked }) {
  const [types, setTypes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [f, setF] = useState({ meeting_type_id: "", assigned_staff_id: "", date: "", start_time: "", timezone: "America/New_York", contact_name: "", contact_email: "" });
  const [slots, setSlots] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    api.get("/scheduling/meeting-types").then((r) => setTypes(r.data.meeting_types || []));
    api.get("/scheduling/staff").then((r) => setStaff(r.data.staff || []));
  }, []);

  const loadSlots = useCallback(() => {
    if (!f.meeting_type_id || !f.date) return;
    setSlots(null);
    api.get("/scheduling/slots", { params: { meeting_type_id: f.meeting_type_id, date: f.date, staff_id: f.assigned_staff_id || undefined } })
      .then((r) => { const sb = r.data.slots_by_staff || {}; const key = f.assigned_staff_id || Object.keys(sb)[0]; setSlots(sb[key] || []); })
      .catch(() => setSlots([]));
  }, [f.meeting_type_id, f.date, f.assigned_staff_id]);
  useEffect(() => { loadSlots(); }, [loadSlots]);

  const book = async () => {
    setBusy(true);
    try { await api.post("/scheduling/appointments", f); toast.success("Appointment booked"); onBooked(); }
    catch (e) { toast.error(e.response?.data?.detail || "Booking failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-5 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="appt-book-modal">
        <div className="flex items-center justify-between"><h3 className="font-heading font-semibold text-slate-800">Book Appointment</h3><button onClick={onClose}><Icons.X className="h-4 w-4 text-slate-400" /></button></div>
        <div><label className={lbl}>Meeting Type</label>
          <select className={inp} data-testid="appt-type-select" value={f.meeting_type_id} onChange={(e) => set("meeting_type_id", e.target.value)}>
            <option value="">Select…</option>{types.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.duration_min}m{t.zoom_required ? " · Zoom" : ""})</option>)}
          </select></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Staff</label><select className={inp} data-testid="appt-staff-select" value={f.assigned_staff_id} onChange={(e) => set("assigned_staff_id", e.target.value)}><option value="">Auto</option>{staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          <div><label className={lbl}>Date</label><input type="date" className={inp} data-testid="appt-date" value={f.date} onChange={(e) => set("date", e.target.value)} /></div>
        </div>
        {f.meeting_type_id && f.date && (
          <div><label className={lbl}>Available Times {slots === null ? "(loading…)" : `(${slots.length})`}</label>
            <div className="flex flex-wrap gap-1.5" data-testid="appt-slots">
              {slots === null ? <Icons.Loader2 className="h-4 w-4 animate-spin text-blue-500" /> :
               slots.length === 0 ? <span className="text-xs text-slate-400">No open slots — try another day/staff.</span> :
               slots.map((s) => <button key={s} onClick={() => set("start_time", s)} data-testid={`appt-slot-${s}`} className={`text-xs px-2.5 py-1.5 rounded-lg border ${f.start_time === s ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300 hover:border-blue-400"}`}>{s}</button>)}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><label className={lbl}>Contact Name</label><input className={inp} data-testid="appt-contact-name" value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></div>
          <div><label className={lbl}>Contact Email</label><input className={inp} value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></div>
        </div>
        <button onClick={book} disabled={busy || !f.meeting_type_id || !f.date || !f.start_time} data-testid="appt-book-submit" className="w-full bg-[#1f6feb] text-white rounded-lg py-2.5 text-sm font-medium disabled:opacity-50">{busy ? "Booking…" : "Book Appointment"}</button>
      </div>
    </div>
  );
}
